import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, get, patch, post } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { PageHeader } from '../components/AppShell.tsx'
import { Combobox, type Option } from '../components/Combobox.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Field, Input, Select, Skeleton, Textarea } from '../components/ui.tsx'
import { IconAlert, IconEye, IconEdit, IconPlus, IconTrash } from '../components/icons.tsx'
import { TIMES_PER_DAY } from '@pharmacia/core'

/**
 * One instruction: this product, this much, this many times a day.
 *
 * `quantity` is the dose taken on EACH occasion. Five capsules morning and
 * evening is quantity 5, timesPerDay 2 -- not quantity 10 -- so the
 * prescription still says what the doctor said. Billing multiplies.
 */
import {
  addDays, blankItem, coursePerDay, courseTotal, coursesFromLines, daysBetween,
  expandCourses, fmtQty, overlaps, today, uid, type Course, type Item,
} from '../lib/courses.ts'
import { CourseDetail, CourseEditor } from '../components/CourseEditor.tsx'
import { formatDate } from '../lib/format.ts'

export function PrescriptionFormPage() {
  const { id } = useParams()
  const editing = Boolean(id)
  const navigate = useNavigate()
  const toast = useToast()
  const { me } = useAuth()
  const isOwner = me?.staff.role === 'owner'

  const [doctor, setDoctor] = useState<Option | null>(null)
  const [patient, setPatient] = useState<Option | null>(null)
  const [remarks, setRemarks] = useState('')
  // Two tables, because they answer different questions. The schedule
  // says what to take on which day -- the dose changes day to day, so one
  // quantity per product cannot express it. General medicines are
  // dispensed once and carry no date.
  // Courses, not days. "10th to 15th, five Kamraj and four Jeevan Viswas"
  // is one row here and six days in the database. A single day is a course
  // whose start and end are the same date.
  const [courses, setCourses] = useState<Course[]>([])
  const [general, setGeneral] = useState<Item[]>([blankItem(1)])
  /** Which course is open, and whether for editing or just reading. */
  const [editingCourse, setEditingCourse] = useState<Course | null>(null)
  const [viewing, setViewing] = useState<number | null>(null)

  const [status, setStatus] = useState<'draft' | 'sent'>('draft')
  const [locked, setLocked] = useState<{ locked: boolean; reason: string | null }>(
    { locked: false, reason: null })
  const [version, setVersion] = useState(0)
  const [loading, setLoading] = useState(editing)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fixed for the life of the form, so a retry after a lost response
  // replays the same id rather than writing a second prescription.
  const newId = useMemo(() => crypto.randomUUID(), [])

  // A doctor writes in their own name and the server enforces it, so the
  // picker is pre-filled and left alone.
  useEffect(() => {
    if (!editing && !isOwner && me) {
      setDoctor({ id: me.staff.id, label: me.staff.fullName, sub: me.staff.email })
    }
  }, [editing, isOwner, me])

  useEffect(() => {
    if (!editing) return
    let cancelled = false
    get<{
      prescription: Record<string, unknown>
      lines: Record<string, unknown>[]
      locked: boolean; reason: string | null
    }>(`/api/prescriptions/${id}`)
      .then(d => {
        if (cancelled) return
        const p = d.prescription
        setDoctor({ id: String(p['doctor_staff_id']), label: String(p['doctor_name']),
                    sub: String(p['doctor_email'] ?? '') })
        setPatient({ id: String(p['patient_id']), label: String(p['patient_name']),
                     sub: String(p['mrn'] ?? '') })
        setRemarks(String(p['remarks'] ?? ''))
        setVersion(Number(p['version']))
        setStatus((p['status'] as 'draft' | 'sent') ?? 'draft')
        setLocked({ locked: d.locked, reason: d.reason })
        const asItem = (l: Record<string, unknown>): Item => ({
          key: crypto.randomUUID(),
          product: { id: String(l['product_id']), label: String(l['product_name']) },
          quantity: String(Number(l['quantity'])),
          // Rows written before this field existed come back as 1, which is
          // what they have always meant: that quantity, once a day.
          timesPerDay: Number(l['doses_per_day'] ?? 1),
        })
        // Flat rows come back from the API; the form rebuilds the courses
        // they came from, so a prescription written before courses existed
        // still opens as something editable.
        setCourses(coursesFromLines(d.lines))
        const gen = d.lines.filter(l => !l['dose_date']).map(asItem)
        setGeneral(gen.length ? gen : [blankItem(1)])
      })
      .catch((e: ApiError) => { if (!cancelled) toast.fail('Could not load prescription', e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, editing, toast])

  const editGeneral = (key: string, p: Partial<Item>) =>
    setGeneral(ls => ls.map(l => (l.key === key ? { ...l, ...p } : l)))
  const saveCourse = (c: Course) => {
    setCourses(cs => cs.some(x => x.key === c.key)
      ? cs.map(x => (x.key === c.key ? c : x))
      : [...cs, c])
    setEditingCourse(null)
    setErrors({})
  }

  async function save(andSend: boolean) {
    setErrors({})

    // Courses become one row per medicine per day. The grouping is a
    // property of the form; the expansion is what the invoice is built
    // from and what the register has to show.
    const scheduled = expandCourses(courses)
    const generalLines = general.filter(i => i.product).map(i => ({
      productId: i.product!.id,
      quantity: Number(i.quantity),
      dosesPerDay: 1,
      doseDate: null as string | null,
    }))
    const lines = [...scheduled, ...generalLines]

    if (!patient) { setErrors({ patient: 'Choose a patient' }); return }
    if (!doctor) { setErrors({ doctor: 'Choose a doctor' }); return }
    if (lines.length === 0) { setErrors({ lines: 'Add at least one course or general medicine' }); return }
    if (lines.some(l => !(l.quantity > 0))) {
      setErrors({ lines: 'Every medicine needs a dose above zero' }); return
    }
    if (general.some(i => i.product && !(Number(i.quantity) > 0))) {
      setErrors({ lines: 'Every general medicine needs a quantity above zero' }); return
    }
    // Two courses claiming the same medicine on the same day: the later one
    // silently wins the expansion, so say so rather than let it happen.
    const clashes = overlaps(courses)
    if (clashes.length) {
      setErrors({ lines: `Two courses cover the same medicine on the same day — ${clashes.slice(0, 3).join('; ')}${clashes.length > 3 ? ` and ${clashes.length - 3} more` : ''}` })
      return
    }
    if (lines.length > 400) {
      setErrors({ lines: `That comes to ${lines.length} rows. A prescription holds at most 400 — shorten a course or split the prescription.` })
      return
    }

    setBusy(true)
    try {
      const body = {
        patientId: patient.id,
        doctorStaffId: doctor.id,
        remarks: remarks.trim() || null,
        lines,
      }
      const targetId = editing ? String(id) : newId
      let nextVersion = version
      if (editing) {
        await patch(`/api/prescriptions/${id}`, { ...body, version })
        // Editing something already sent withdraws it, so the version
        // moved more than once. Read it back rather than guessing.
        const fresh = await get<{ prescription: Record<string, unknown> }>(`/api/prescriptions/${id}`)
        nextVersion = Number(fresh.prescription['version'])
      } else {
        await post('/api/prescriptions', { id: newId, ...body })
        const fresh = await get<{ prescription: Record<string, unknown> }>(`/api/prescriptions/${newId}`)
        nextVersion = Number(fresh.prescription['version'])
      }

      if (andSend) {
        await post(`/api/prescriptions/${targetId}/send`, { version: nextVersion })
        toast.ok('Sent to billing', 'The counter can now raise the bill.')
      } else {
        toast.ok(editing ? 'Prescription updated' : 'Prescription written')
      }
      navigate(`/prescriptions/${targetId}`)
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'VALIDATION_FAILED') setErrors(err.fieldErrors)
      else if (err.code === 'STALE_WRITE') toast.fail('Someone else saved first', err.message)
      else toast.fail('Could not save', err.message)
    } finally { setBusy(false) }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Prescription" />
        <div className="content"><div className="page-max card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} h={24} />)}
        </div></div></div>
      </>
    )
  }

  return (
    <>
      <PageHeader title={editing ? 'Edit prescription' : 'New prescription'}>
        {status === 'sent' ? <span className="badge final">With the counter</span> : null}
      </PageHeader>
      <div className="content">
        {locked.locked ? (
          <div className="page-max notice" role="alert" style={{ maxWidth: 880, marginBottom: 'var(--s-5)' }}>
            <span style={{ color: 'var(--danger)', marginTop: 1 }}><IconAlert size={15} /></span>
            <span>
              <strong>This prescription can no longer be changed.</strong> {locked.reason}
            </span>
          </div>
        ) : null}
        <form className="page-max card" style={{ maxWidth: 880 }}
          onSubmit={e => { e.preventDefault(); void save(false) }}>
          <div className="card-body">
            <section className="form-section">
              <h3>Who</h3>
              <div className="form-grid">
                <Field label="Doctor" error={errors['doctor'] ?? errors['doctorStaffId']}
                  hint={isOwner ? 'Search registered doctors' : 'Prescriptions are written in your own name'}>
                  <Combobox endpoint="/api/lookup/doctors" value={doctor} onChange={setDoctor}
                    placeholder="Type a doctor's name" disabled={!isOwner} />
                </Field>
                <Field label="Patient" error={errors['patient'] ?? errors['patientId']}
                  hint="Search by name, MRN or phone">
                  <Combobox endpoint="/api/lookup/patients" value={patient} onChange={setPatient}
                    placeholder="Type a patient's name" autoFocus={isOwner ? false : true} />
                </Field>
              </div>
            </section>

            <section className="form-section">
              <h3>Doctor's remarks</h3>
              <Field label="Remarks" htmlFor="rx-remarks" error={errors['remarks']}
                hint="Dosage, advice, follow-up — anything the patient should be told">
                <Textarea id="rx-remarks" value={remarks} maxLength={4000}
                  onChange={e => setRemarks(e.target.value)} rows={5} />
              </Field>
            </section>
            <section className="form-section">
              <h3>Dosing schedule</h3>
              <p className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--s-4)' }}>
                One row per course. A course covers a span of dates and holds
                however many medicines were prescribed together — the 10th to the
                15th, five Kamraj and four Jeevan Viswas, morning and evening. For
                a single day, set the same date twice.
              </p>

              {errors['lines'] ? (
                <p style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', marginBottom: 'var(--s-3)' }}>
                  {errors['lines']}
                </p>
              ) : null}

              {courses.length === 0 ? (
                <div className="empty" style={{ marginBottom: 'var(--s-3)' }}>
                  <div className="t">No courses yet</div>
                  <div className="s">Add one to say what is taken, and between which dates.</div>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data courses">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }}>#</th>
                        <th>From</th><th>To</th>
                        <th style={{ width: 62 }}>Days</th>
                        <th>Medicines</th>
                        <th style={{ width: 86 }}>A day</th>
                        <th style={{ width: 86 }}>Total</th>
                        <th style={{ width: 104 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((c, i) => (
                        <tr key={c.key}>
                          <td>{i + 1}</td>
                          <td>{formatDate(c.from)}</td>
                          <td>{formatDate(c.to)}</td>
                          <td>{daysBetween(c.from, c.to)}</td>
                          <td>
                            {c.items.filter(it => it.product).map(it => (
                              <div key={it.key} className="med">
                                {it.product!.label}
                                <span className="dim">
                                  {' '}{fmtQty(Number(it.quantity))} × {it.timesPerDay}
                                  {' = '}{fmtQty(Number(it.quantity) * it.timesPerDay)} a day
                                </span>
                              </div>
                            ))}
                          </td>
                          <td>{fmtQty(coursePerDay(c))}</td>
                          <td><b>{fmtQty(courseTotal(c))}</b></td>
                          <td className="actions">
                            <button type="button" className="icon-btn" title="See every day"
                              aria-label={`See every day of course ${i + 1}`}
                              onClick={() => setViewing(i)}>
                              <IconEye size={15} />
                            </button>
                            <button type="button" className="icon-btn" title="Change this course"
                              aria-label={`Change course ${i + 1}`} disabled={locked.locked}
                              onClick={() => setEditingCourse(c)}>
                              <IconEdit size={15} />
                            </button>
                            <button type="button" className="icon-btn danger" title="Remove this course"
                              aria-label={`Remove course ${i + 1}`} disabled={locked.locked}
                              onClick={() => setCourses(cs => cs.filter(x => x.key !== c.key))}>
                              <IconTrash size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={6}><b>Dispensed across every course</b></td>
                        <td colSpan={2}>
                          <b>{fmtQty(courses.reduce((n, c) => n + courseTotal(c), 0))}</b>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <Button type="button" style={{ marginTop: 'var(--s-3)' }} disabled={locked.locked}
                onClick={() => setEditingCourse({
                  key: uid(),
                  // A new course starts the day after the last one ends, which
                  // is almost always what a taper means.
                  from: courses.length ? addDays(courses[courses.length - 1]!.to, 1) : today(),
                  to: courses.length ? addDays(courses[courses.length - 1]!.to, 7) : addDays(today(), 6),
                  items: [blankItem()],
                })}>
                <IconPlus /> Add course
              </Button>
            </section>

            <section className="form-section">
              <h3>General medicines</h3>
              <p className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--s-4)' }}>
                Dispensed once, with no particular day attached.
              </p>
              <div className="table-wrap">
              <table className="data lines">
                <thead>
                  <tr><th style={{ width: 40 }}>#</th><th>Product</th>
                    <th style={{ width: 130 }}>Quantity</th><th style={{ width: 44 }} /></tr>
                </thead>
                <tbody>
                  {general.map((l, i) => (
                    <tr key={l.key}>
                      <td className="dim">{i + 1}</td>
                      <td>
                        <Combobox endpoint="/api/lookup/products" value={l.product}
                          onChange={o => editGeneral(l.key, { product: o })}
                          disabled={locked.locked} placeholder="Type a product name" />
                      </td>
                      <td>
                        <Input value={l.quantity} inputMode="decimal" disabled={locked.locked}
                          onChange={e => editGeneral(l.key, { quantity: e.target.value })}
                          aria-label={`Quantity for general row ${i + 1}`} />
                      </td>
                      <td className="rm">
                        <button type="button" className="icon-btn danger" title="Remove row"
                          aria-label={`Remove general row ${i + 1}`}
                          disabled={general.length === 1 || locked.locked}
                          onClick={() => setGeneral(ls => ls.filter(x => x.key !== l.key))}>
                          <IconTrash size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <Button type="button" style={{ marginTop: 'var(--s-3)' }} disabled={locked.locked}
                onClick={() => setGeneral(ls => [...ls, blankItem(1)])}>
                <IconPlus /> Add row
              </Button>
            </section>
          </div>

          <div className="form-actions">
            <Button type="button" variant="primary" loading={busy} disabled={locked.locked}
              onClick={() => void save(true)}>
              {status === 'sent' ? 'Update and send to billing' : 'Save and send to billing'}
            </Button>
            <Button type="button" loading={busy} disabled={locked.locked}
              onClick={() => void save(false)}>
              {editing ? 'Save changes' : 'Save as draft'}
            </Button>
            <Button type="button" variant="ghost" disabled={busy}
              onClick={() => navigate(editing ? `/prescriptions/${id}` : '/prescriptions')}>
              Cancel
            </Button>
            <span className="grow" />
            <span className="dim" style={{ fontSize: 'var(--text-xs)' }}>
              No prices here — the counter bills from this
            </span>
          </div>
        </form>
      </div>
      {editingCourse ? (
        <CourseEditor course={editingCourse} onSave={saveCourse}
          onClose={() => setEditingCourse(null)} />
      ) : null}
      {viewing !== null && courses[viewing] ? (
        <CourseDetail course={courses[viewing]!} index={viewing}
          onClose={() => setViewing(null)} />
      ) : null}

    </>
  )
}
